import { ImageSegmenterResult } from '@mediapipe/tasks-vision';
import { MediaStreamVideoConsumer, MediaStreamVideoSupplier } from 'mind-sdk-web/lib/MediaStream';
import { MediaStreamVideoBuffer } from 'mind-sdk-web/lib/MediaStreamVideoBuffer';
import { BLUR_AMOUNT, BLUR_FRAME_RATE } from './background-blurer';
import { WebGLRenderer } from './webgl-renderer';

export class WebGLVideoSupplier implements MediaStreamVideoSupplier {
  private consumers: Set<MediaStreamVideoConsumer> = new Set();
  private readonly track: MediaStreamTrack | null = null;
  private renderer: WebGLRenderer;

  // Default values for video parameters
  private bitrate: number = 1000000; // 1Mbps default bitrate
  private readonly adaptivity: number;
  private readonly frameRate: number;
  private blurAmount: number;

  constructor(
    canvas: HTMLCanvasElement,
    frameRate: number = BLUR_FRAME_RATE,
    blurAmount: number = BLUR_AMOUNT,
    adaptivity: number = 1
  ) {
    this.renderer = new WebGLRenderer(canvas);

    this.frameRate = frameRate;
    this.blurAmount = blurAmount;
    this.adaptivity = adaptivity;

    const stream = canvas.captureStream(this.frameRate);
    this.track = stream.getVideoTracks()[0];
    this.waitForTrack(this.track).then(() => this.fireVideoBuffer());
  }

  waitForTrack(track: MediaStreamTrack): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Set timeout to limit waiting to 1 minute
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for track to become live'));
      }, 60000);

      const checkTrackReady = () => {
        if (track.readyState === 'live') {
          // Small delay to ensure track is fully initialized
          setTimeout(() => {
            clearTimeout(timeout);
            resolve();
          }, 100);
        } else {
          setTimeout(checkTrackReady, 100);
        }
      };

      checkTrackReady();
    });
  }

  addVideoConsumer(consumer: MediaStreamVideoConsumer): void {
    this.consumers.add(consumer);
    if (this.track) {
      const canvas = this.renderer.getCanvas();
      const buffer = new MediaStreamVideoBuffer(
        this.track,
        false,
        canvas.width,
        canvas.height,
        this.bitrate,
        this.adaptivity,
        1.0
      );
      consumer.onVideoBuffer(buffer, this);
    }
  }

  removeVideoConsumer(consumer: MediaStreamVideoConsumer): void {
    this.consumers.delete(consumer);
  }

  fireVideoBuffer(): void {
    if (!this.track) {
      console.error('WebGLVideoSupplier: No video track available');
      return;
    }

    const canvas = this.renderer.getCanvas();
    const buffer = new MediaStreamVideoBuffer(
      this.track,
      false,
      canvas.width,
      canvas.height,
      this.bitrate,
      this.adaptivity,
      1.0
    );
    this.consumers.forEach((consumer) => consumer.onVideoBuffer(buffer, this));
  }

  drawFrame(image: HTMLVideoElement, segmentationResult: ImageSegmenterResult): void {
    // Confidence mask (most precise)
    if (segmentationResult.confidenceMasks?.[0]) {
      const segmentationMask = segmentationResult.confidenceMasks[0].getAsWebGLTexture();
      this.renderer.renderFrame(image, segmentationMask, this.blurAmount, false);
      return;
    }

    // Category mask: try as WebGL texture
    if (segmentationResult.categoryMask?.getAsWebGLTexture) {
      const maskTexture = segmentationResult.categoryMask.getAsWebGLTexture();
      this.renderer.renderFrame(image, maskTexture, this.blurAmount, true);
      return;
    }

    console.warn('[WebGLBlur] No valid mask found in segmentationResult');
  }

  setBlurAmount(amount: number): void {
    this.blurAmount = amount;
    this.fireVideoBuffer();
  }
}
